import { Alert, Card, CardContent, Spinner } from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useFeature } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { HintMarkerBadge, NutriScoreBadge } from './badges';
import { NutritionTable } from './product-card';

/**
 * Nutrition and the estimated Nutri-Score of a recipe (docs/finished/ingredient-catalog-spec.md 6.6,
 * 6.10). The score is the app's own estimate, computed from the recipe's nutrition per 100 g –
 * it is labelled as such, states its coverage and is withheld when too little is known.
 */
export function RecipeNutritionCard({ recipeId, enabled }: { recipeId: string; enabled: boolean }) {
  const { t } = useTranslation();
  const catalogEnabled = useFeature('ingredient_catalog');
  const nutritionEnabled = useFeature('recipe_nutrition');
  // The card sits below the recipe: it asks the API only once it scrolls into view, so its
  // requests never compete with what the user is doing at the top of the page.
  const anchor = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const element = anchor.current;
    if (!element || inView) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setInView(true);
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [inView]);
  const active = enabled && catalogEnabled && inView;

  const links = useQuery({
    queryKey: queryKeys.catalog.recipeLinks(recipeId),
    queryFn: () => api.catalog.recipeLinks(recipeId),
    enabled: active,
    retry: false,
  });
  const nutrition = useQuery({
    queryKey: queryKeys.catalog.recipeNutrition(recipeId),
    queryFn: () => api.catalog.recipeNutrition(recipeId),
    enabled: active && nutritionEnabled,
    retry: false,
  });
  // Provider lookups run in the background; look again once while they are pending.
  const pending = nutrition.data?.pending === true;
  const refetch = nutrition.refetch;
  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(() => void refetch(), 6000);
    return () => clearTimeout(timer);
  }, [pending, refetch]);

  if (!enabled || !catalogEnabled) return null;
  if (!inView) return <div ref={anchor} aria-hidden className="h-px" />;
  const data = nutrition.data;
  const linked = (links.data?.links ?? []).filter((l) => l.ingredient);
  if (!data && linked.length === 0)
    return nutrition.isLoading ? <Spinner label={t('app.loading')} /> : null;

  const unresolved = data?.ingredients.filter((i) => i.status !== 'COUNTED') ?? [];

  return (
    <Card data-testid="recipe-nutrition">
      <CardContent className="space-y-3 p-4">
        <h2 className="font-semibold">{t('catalog.recipe.title')}</h2>

        {data && nutritionEnabled && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              {data.nutriScoreEstimate ? (
                <NutriScoreBadge grade={data.nutriScoreEstimate} estimated />
              ) : (
                <span
                  className="text-sm text-muted-foreground"
                  data-testid="recipe-nutrition-insufficient"
                >
                  {t('catalog.recipe.notEnoughData', { percent: data.minCoveragePercent })}
                </span>
              )}
              <span
                className="text-xs text-muted-foreground"
                data-testid="recipe-nutrition-coverage"
              >
                {t('catalog.recipe.coverage', {
                  counted: data.coverage.counted,
                  total: data.coverage.total,
                  percent: data.coverage.weightPercent,
                })}
              </span>
            </div>
            {data.perServing && (
              <NutritionTable values={data.perServing} caption={t('catalog.recipe.perServing')} />
            )}
            {unresolved.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground">
                  {t('catalog.recipe.unresolved', { count: unresolved.length })}
                </summary>
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {unresolved.map((i) => (
                    <li key={i.recipeIngredientId}>
                      {i.name} – {t(`catalog.recipe.status.${i.status}`)}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {pending && (
              <p className="text-xs text-muted-foreground">{t('catalog.recipe.pending')}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {t('catalog.recipe.estimateInfo')}
              {data.providers.length > 0 &&
                ` ${t('catalog.recipe.sources', {
                  sources: data.providers.map((p) => p.attribution ?? p.key).join('; '),
                })}`}
            </p>
          </>
        )}
        {nutrition.error && !data && (
          <Alert variant="info">{t('catalog.recipe.unavailable')}</Alert>
        )}

        {linked.length > 0 && (
          <ul className="divide-y rounded-md border" data-testid="recipe-ingredient-links">
            {linked.map((link) => (
              <li
                key={link.recipeIngredientId}
                className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-sm"
              >
                <Link
                  to={`/catalog/ingredients/${link.ingredient!.id}`}
                  className="min-w-0 flex-1 truncate underline-offset-2 hover:underline"
                >
                  {link.name}
                </Link>
                <HintMarkerBadge marker={link.marker} />
                {link.product && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    {link.product.name}
                    {link.product.nutriScore && (
                      <NutriScoreBadge grade={link.product.nutriScore} size="sm" />
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
